import { useState, useEffect } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/ui/table';
import { Checkbox } from '../../../components/ui/checkbox';
import { Card, CardContent } from '../../../components/ui/card';
import { Switch } from '../../../components/ui/switch';
import { toast } from 'sonner';
import { 
  RefreshCw, CheckCircle, Edit, Trash2, Send, 
  ArrowUpRight, ArrowDownLeft, BookOpen, Tag, 
  AlertTriangle, Copy, CheckCheck, X
} from 'lucide-react';

// Chart of Accounts categories
const CATEGORIES = [
  { code: 'AP', name: 'AP', type: 'liability' },
  { code: 'BANK_FEES', name: 'Bank Fees', type: 'expense' },
  { code: 'BILLING_AGENT', name: 'Billing Agent', type: 'expense' },
  { code: 'CLEANING', name: 'Cleaning', type: 'expense' },
  { code: 'CRA_2023', name: 'CRA Payment - 2023 Accr', type: 'expense' },
  { code: 'CRA_RP_2025', name: 'CRA RP - 2025', type: 'expense' },
  { code: 'DELIVERY', name: 'Delivery', type: 'expense' },
  { code: 'DOCTOR_CARE', name: 'Doctor Care', type: 'expense' },
  { code: 'EMR', name: 'EMR', type: 'expense' },
  { code: 'FACILITY_MAINT', name: 'Facility Maintenance', type: 'expense' },
  { code: 'FOOD_ENT', name: 'Food and Entertainment', type: 'expense' },
  { code: 'INSURANCE', name: 'Insurance', type: 'expense' },
  { code: 'IT_SERVICES', name: 'IT Services', type: 'expense' },
  { code: 'LOAN_REPAY', name: 'Loan Repayment', type: 'liability' },
  { code: 'LOAN_GSAMRA', name: 'Loan Repayment - Gsamra', type: 'liability' },
  { code: 'LOAN_GMG_CONST', name: 'Loan to GMG - Const', type: 'asset' },
  { code: 'LOAN_GMG_ZEBO', name: 'Loan to GMG - ZebOville', type: 'asset' },
  { code: 'LOAN_PHARMACY', name: 'Loan to Pharmacy', type: 'asset' },
  { code: 'MISC', name: 'Misc', type: 'expense' },
  { code: 'PAYROLL_EMP', name: 'Payroll - Employee', type: 'expense' },
  { code: 'PHONE_INTERNET', name: 'Phone & Internet', type: 'expense' },
  { code: 'RENT', name: 'Rent', type: 'expense' },
  { code: 'SUPPLIES', name: 'Supplies', type: 'expense' },
  { code: 'TRAVEL', name: 'Travel', type: 'expense' },
  { code: 'UTILITIES', name: 'Utilities', type: 'expense' },
  { code: 'REV_DOCTORS', name: 'Revenue - Doctors', type: 'revenue' },
  { code: 'REV_UNINSURED', name: 'Revenue - Uninsured', type: 'revenue' },
  { code: 'BANK_CLEARING', name: 'Bank / Cash Clearing', type: 'asset' },
];

export default function ParsedTransactions() {
  const { api } = useAuth();
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState([]);
  const [filterStatus, setFilterStatus] = useState('all');
  
  // Dialogs
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showBulkCategoryDialog, setShowBulkCategoryDialog] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showDuplicates, setShowDuplicates] = useState(false);
  
  // Edit form
  const [editingTxn, setEditingTxn] = useState(null);
  const [editForm, setEditForm] = useState({
    category_code: '',
    description: '',
    vendor: ''
  });
  
  // Bulk category form
  const [bulkCategoryCode, setBulkCategoryCode] = useState('');
  const [saveVendorMapping, setSaveVendorMapping] = useState(true);
  
  // Duplicates
  const [duplicates, setDuplicates] = useState([]);

  useEffect(() => {
    fetchTransactions();
  }, [filterStatus]);

  const fetchTransactions = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterStatus && filterStatus !== 'all') {
        params.append('review_status', filterStatus);
      }
      const res = await api.get(`/finance/parsed-transactions?${params.toString()}`);
      setTransactions(res.data);
      setSelectedIds([]);
    } catch (error) {
      console.error('Failed to fetch parsed transactions:', error);
      toast.error('Failed to load transactions');
    } finally {
      setLoading(false);
    }
  };

  // Selection handlers
  const handleSelectAll = (checked) => {
    if (checked) {
      const selectableIds = transactions
        .filter(t => t.review_status !== 'posted')
        .map(t => t.id);
      setSelectedIds(selectableIds);
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectOne = (id, checked) => {
    if (checked) {
      setSelectedIds([...selectedIds, id]);
    } else {
      setSelectedIds(selectedIds.filter(i => i !== id));
    }
  };

  const clearSelection = () => setSelectedIds([]);

  // Edit handlers
  const openEditDialog = (txn) => {
    setEditingTxn(txn);
    setEditForm({
      category_code: txn.category_code || txn.suggested_category_code || '',
      description: txn.description || '',
      vendor: txn.vendor || ''
    });
    setShowEditDialog(true);
  };

  const handleSaveEdit = async () => {
    if (!editingTxn) return;
    
    try {
      await api.patch(`/finance/parsed-transactions/${editingTxn.id}`, editForm);
      toast.success('Transaction updated');
      setShowEditDialog(false);
      setEditingTxn(null);
      fetchTransactions();
    } catch (error) {
      toast.error('Failed to update transaction');
    }
  };

  // Single transaction actions
  const handleConfirm = async (txnId) => {
    try {
      await api.post(`/finance/parsed-transactions/${txnId}/confirm`);
      toast.success('Transaction confirmed');
      fetchTransactions();
    } catch (error) {
      toast.error('Failed to confirm transaction');
    }
  };

  const handleDelete = async (txnId) => {
    if (!confirm('Delete this transaction?')) return;
    
    try {
      await api.delete(`/finance/parsed-transactions/${txnId}`);
      toast.success('Transaction deleted');
      fetchTransactions();
    } catch (error) {
      toast.error('Failed to delete transaction');
    }
  };

  // Bulk actions
  const handleBulkConfirm = async () => {
    if (selectedIds.length === 0) {
      toast.error('No transactions selected');
      return;
    }
    
    try {
      const res = await api.post('/finance/parsed-transactions/bulk-confirm', {
        transaction_ids: selectedIds
      });
      toast.success(`Confirmed ${res.data.message || selectedIds.length + ' transactions'}`);
      fetchTransactions();
    } catch (error) {
      toast.error('Failed to confirm transactions');
    }
  };

  const handleBulkReview = async () => {
    if (selectedIds.length === 0) {
      toast.error('No transactions selected');
      return;
    }
    
    try {
      const res = await api.post('/finance/parsed-transactions/bulk-review', {
        transaction_ids: selectedIds
      });
      toast.success(res.data.message);
      fetchTransactions();
    } catch (error) {
      toast.error('Failed to mark transactions as reviewed');
    }
  };

  const handleBulkDelete = async () => {
    try {
      const res = await api.post('/finance/parsed-transactions/bulk-delete', {
        transaction_ids: selectedIds
      });
      toast.success(`Deleted ${res.data.deleted_count} transactions`);
      setShowDeleteConfirm(false);
      fetchTransactions();
    } catch (error) {
      toast.error('Failed to delete transactions');
    }
  };

  const handleBulkCategoryAssign = async () => {
    if (!bulkCategoryCode) {
      toast.error('Please select a category');
      return;
    }
    
    try {
      const res = await api.post('/finance/parsed-transactions/bulk-category', {
        transaction_ids: selectedIds,
        category_code: bulkCategoryCode,
        save_vendor_mapping: saveVendorMapping
      });
      toast.success(`Updated ${res.data.updated_count} transactions`);
      if (res.data.vendors_learned > 0) {
        toast.info(`Learned ${res.data.vendors_learned} vendor mappings for future`);
      }
      setShowBulkCategoryDialog(false);
      setBulkCategoryCode('');
      fetchTransactions();
    } catch (error) {
      toast.error('Failed to assign category');
    }
  };

  const handlePostToLedger = async () => {
    const confirmedIds = transactions
      .filter(t => selectedIds.includes(t.id) && t.review_status === 'confirmed')
      .map(t => t.id);
    
    if (confirmedIds.length === 0) {
      toast.error('No confirmed transactions to post. Confirm transactions first.');
      return;
    }
    
    try {
      const res = await api.post('/finance/ledger/post', {
        transaction_ids: confirmedIds
      });
      toast.success(`Posted ${res.data.posted_count} transactions to ledger`);
      if (res.data.errors?.length > 0) {
        toast.warning(`${res.data.errors.length} errors occurred`);
      }
      fetchTransactions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to post to ledger');
    }
  };

  // Duplicate detection
  const checkDuplicates = async () => {
    try {
      const res = await api.get('/finance/parsed-transactions/duplicates');
      setDuplicates(res.data.duplicates || []);
      setShowDuplicates(true);
      if (res.data.duplicate_count === 0) {
        toast.success('No duplicates found');
      } else {
        toast.warning(`Found ${res.data.duplicate_count} potential duplicates`);
      }
    } catch (error) {
      toast.error('Failed to check duplicates');
    }
  };

  // Helpers
  const getStatusBadge = (status) => {
    const config = {
      pending: { color: 'bg-amber-100 text-amber-700', label: 'Pending' },
      reviewed: { color: 'bg-blue-100 text-blue-700', label: 'Reviewed' },
      confirmed: { color: 'bg-emerald-100 text-emerald-700', label: 'Confirmed' },
      posted: { color: 'bg-slate-100 text-slate-700', label: 'Posted' },
      rejected: { color: 'bg-red-100 text-red-700', label: 'Rejected' },
    };
    const c = config[status] || config.pending;
    return <Badge className={c.color}>{c.label}</Badge>;
  };

  const getCategoryName = (code) => {
    const cat = CATEGORIES.find(c => c.code === code);
    return cat ? cat.name : code || 'Uncategorized';
  };

  // Stats
  const pendingCount = transactions.filter(t => t.review_status === 'pending').length;
  const reviewedCount = transactions.filter(t => t.review_status === 'reviewed').length;
  const confirmedCount = transactions.filter(t => t.review_status === 'confirmed').length;
  const selectableCount = transactions.filter(t => t.review_status !== 'posted').length;
  const selectedTotal = transactions
    .filter(t => selectedIds.includes(t.id))
    .reduce((sum, t) => sum + t.amount, 0);

  return (
    <div className="space-y-4" data-testid="parsed-transactions">
      {/* Summary Bar */}
      <div className="flex flex-wrap justify-between items-center gap-4 p-3 bg-slate-50 rounded-lg">
        <div className="flex gap-4 text-sm">
          <span>Total: <strong>{transactions.length}</strong></span>
          <span className="text-amber-600">Pending: <strong>{pendingCount}</strong></span>
          <span className="text-blue-600">Reviewed: <strong>{reviewedCount}</strong></span>
          <span className="text-emerald-600">Confirmed: <strong>{confirmedCount}</strong></span>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={checkDuplicates} title="Check for duplicates">
            <Copy className="h-4 w-4 mr-1" />
            Duplicates
          </Button>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-[140px]">
              <SelectValue placeholder="All Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="reviewed">Reviewed</SelectItem>
              <SelectItem value="confirmed">Confirmed</SelectItem>
              <SelectItem value="posted">Posted</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={fetchTransactions}>
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Bulk Action Bar - Shows when items selected */}
      {selectedIds.length > 0 && (
        <Card className="border-blue-200 bg-blue-50">
          <CardContent className="py-3">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <span className="text-sm font-medium text-blue-700">
                  {selectedIds.length} selected (${selectedTotal.toLocaleString()})
                </span>
                <Button variant="ghost" size="sm" onClick={clearSelection}>
                  <X className="h-4 w-4 mr-1" />
                  Clear
                </Button>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button 
                  size="sm" 
                  variant="outline"
                  onClick={() => setShowBulkCategoryDialog(true)}
                >
                  <Tag className="h-4 w-4 mr-1" />
                  Assign Category
                </Button>
                <Button size="sm" variant="outline" onClick={handleBulkReview}>
                  <CheckCheck className="h-4 w-4 mr-1" />
                  Mark Reviewed
                </Button>
                <Button size="sm" variant="outline" onClick={handleBulkConfirm}>
                  <CheckCircle className="h-4 w-4 mr-1" />
                  Confirm
                </Button>
                <Button size="sm" onClick={handlePostToLedger}>
                  <Send className="h-4 w-4 mr-1" />
                  Post to Ledger
                </Button>
                <Button 
                  size="sm" 
                  variant="destructive"
                  onClick={() => setShowDeleteConfirm(true)}
                >
                  <Trash2 className="h-4 w-4 mr-1" />
                  Delete
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Transactions Table */}
      {loading ? (
        <div className="flex justify-center py-8">
          <RefreshCw className="h-8 w-8 animate-spin text-slate-400" />
        </div>
      ) : transactions.length === 0 ? (
        <div className="text-center py-12">
          <BookOpen className="h-12 w-12 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-500">No parsed transactions</p>
          <p className="text-sm text-slate-400">Upload and parse a bank statement to see transactions here</p>
        </div>
      ) : (
        <div className="rounded-lg border overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="w-10">
                  <Checkbox 
                    checked={selectedIds.length === selectableCount && selectableCount > 0}
                    onCheckedChange={handleSelectAll}
                  />
                </TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Vendor</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>Category</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {transactions.map((txn) => (
                <TableRow 
                  key={txn.id} 
                  data-testid={`parsed-txn-${txn.id}`}
                  className={selectedIds.includes(txn.id) ? 'bg-blue-50' : ''}
                >
                  <TableCell>
                    <Checkbox 
                      checked={selectedIds.includes(txn.id)}
                      onCheckedChange={(checked) => handleSelectOne(txn.id, checked)}
                      disabled={txn.review_status === 'posted'}
                    />
                  </TableCell>
                  <TableCell className="text-slate-600 whitespace-nowrap">
                    {txn.transaction_date}
                  </TableCell>
                  <TableCell className="font-medium max-w-[150px] truncate">
                    {txn.vendor}
                  </TableCell>
                  <TableCell className="text-slate-600 max-w-[200px] truncate">
                    {txn.description}
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className="text-xs">
                      {getCategoryName(txn.category_code || txn.suggested_category_code)}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right font-medium whitespace-nowrap">
                    <span className={`flex items-center justify-end gap-1 ${txn.direction === 'inflow' ? 'text-emerald-600' : 'text-red-600'}`}>
                      {txn.direction === 'inflow' ? (
                        <ArrowDownLeft className="h-3 w-3" />
                      ) : (
                        <ArrowUpRight className="h-3 w-3" />
                      )}
                      ${txn.amount?.toLocaleString()}
                    </span>
                  </TableCell>
                  <TableCell>
                    {getStatusBadge(txn.review_status)}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      {txn.review_status === 'pending' && (
                        <Button 
                          variant="ghost" 
                          size="icon"
                          onClick={() => handleConfirm(txn.id)}
                          title="Confirm"
                          className="text-emerald-500 hover:text-emerald-700"
                        >
                          <CheckCircle className="h-4 w-4" />
                        </Button>
                      )}
                      {txn.review_status !== 'posted' && (
                        <>
                          <Button 
                            variant="ghost" 
                            size="icon"
                            onClick={() => openEditDialog(txn)}
                            title="Edit"
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="icon"
                            onClick={() => handleDelete(txn.id)}
                            title="Delete"
                            className="text-red-500 hover:text-red-700"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Transaction</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Vendor</Label>
              <Input
                value={editForm.vendor}
                onChange={(e) => setEditForm({ ...editForm, vendor: e.target.value })}
              />
            </div>
            <div>
              <Label>Description</Label>
              <Input
                value={editForm.description}
                onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
              />
            </div>
            <div>
              <Label>Category</Label>
              <Select 
                value={editForm.category_code} 
                onValueChange={(v) => setEditForm({ ...editForm, category_code: v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  {CATEGORIES.map(cat => (
                    <SelectItem key={cat.code} value={cat.code}>
                      {cat.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500 mt-1">
                Changing category will remember this vendor for future imports
              </p>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setShowEditDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleSaveEdit}>
                Save Changes
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bulk Category Dialog */}
      <Dialog open={showBulkCategoryDialog} onOpenChange={setShowBulkCategoryDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Assign Category to {selectedIds.length} Transactions</DialogTitle>
            <DialogDescription>
              Select a category to assign to all selected transactions.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Category</Label>
              <Select 
                value={bulkCategoryCode} 
                onValueChange={setBulkCategoryCode}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  {CATEGORIES.map(cat => (
                    <SelectItem key={cat.code} value={cat.code}>
                      {cat.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <Switch 
                checked={saveVendorMapping}
                onCheckedChange={setSaveVendorMapping}
              />
              <Label className="text-sm">
                Learn vendor mappings for future auto-categorization
              </Label>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setShowBulkCategoryDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleBulkCategoryAssign}>
                Assign to {selectedIds.length} Transactions
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" />
              Confirm Delete
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete {selectedIds.length} transactions? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteConfirm(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleBulkDelete}>
              Delete {selectedIds.length} Transactions
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Duplicates Dialog */}
      <Dialog open={showDuplicates} onOpenChange={setShowDuplicates}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Potential Duplicates ({duplicates.length})</DialogTitle>
            <DialogDescription>
              Transactions with the same date, amount, and similar vendor are flagged as potential duplicates.
            </DialogDescription>
          </DialogHeader>
          {duplicates.length === 0 ? (
            <p className="text-center py-4 text-slate-500">No duplicates found</p>
          ) : (
            <div className="space-y-4">
              {duplicates.map((dup, i) => (
                <Card key={i} className="border-amber-200">
                  <CardContent className="py-3">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="font-medium text-slate-700">Original</p>
                        <p>{dup.original.transaction_date}</p>
                        <p className="truncate">{dup.original.vendor}</p>
                        <p className="font-medium">${dup.original.amount?.toLocaleString()}</p>
                      </div>
                      <div>
                        <p className="font-medium text-amber-700">Potential Duplicate</p>
                        <p>{dup.duplicate.transaction_date}</p>
                        <p className="truncate">{dup.duplicate.vendor}</p>
                        <p className="font-medium">${dup.duplicate.amount?.toLocaleString()}</p>
                        <Button 
                          size="sm" 
                          variant="destructive" 
                          className="mt-2"
                          onClick={async () => {
                            try {
                              await api.delete(`/finance/parsed-transactions/${dup.duplicate.id}`);
                              toast.success('Duplicate deleted');
                              setDuplicates(duplicates.filter((_, idx) => idx !== i));
                              fetchTransactions();
                            } catch (error) {
                              toast.error('Failed to delete');
                            }
                          }}
                        >
                          Delete Duplicate
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
